/**
 * useCastLifecycleStream — shared hook turning DebugFCT + EventBus into
 * grouped per-cast lifecycle records.
 *
 * Plan: ~/dev/helix/Helix2000/docs/plans/ability-lifecycle-monitor-v2.md
 *
 * Why a hook (not a panel-internal effect): the same stream is useful
 * for AbilityLifecycleMonitor (timeline view) and a future
 * CombatLogPanel "filter by cast" view. Two consumers, one source of
 * truth. Foundation-first per Agent 0's hand-off.
 *
 * Data sources:
 *   - DebugFCT.subscribe() — the canonical cast-pipeline narrative
 *     (intent / gate-X / execute / projectile-* / damage-applied)
 *     emitted by PlayerManager.performAbility + downstream behaviors.
 *     LOCAL casts only — DebugFCT is client-only and only fires for
 *     the local player. Remote-cast tracking is a follow-up.
 *   - EventBus net:message:debugCombat — server-side cast rejections
 *     (e.g. range gate from CombatHandler.handleCastAbility, b0b1603).
 *     Surfaces rejections that the client gate would have caught
 *     anyway, but also catches paths where only the server has
 *     authority (mana mismatch, target despawned mid-cast, anti-cheat).
 *
 * Correlation: by `(abilityId, startTime)` where startTime is the
 * timestamp of the first cast entry for that abilityId after a gap
 * larger than `groupGapMs`. Simple time-clustering — sufficient for
 * local casts since DebugFCT entries are sequential.
 *
 * Anomaly detection (post-completion):
 *   - windup-drift: elapsed `gate-mana → projectile-spawn` mismatched
 *     against the ability's metadata.windUpMs by more than tolerance
 *   - missing-projectile-impact: projectile-spawn fired but no
 *     damage-applied / projectile-hit within 5s
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { DebugFCT, type DebugFCTEntry } from '../../debug/DebugFCT';
import { EventBus } from '../../../shared/EventBus';
import { abilityRegistry } from '../../systems/player/abilities/AbilityRegistry';
import { CastLagStore } from '../../diagnostics/CastLagStore';
import { PhaseTimingTracker } from '../../diagnostics/PhaseTimingTracker';

export type LifecycleCategory = 'cast' | 'vfx' | 'combat' | 'projectile' | 'rejection';
export type LifecycleSource = 'fct' | 'event-bus' | 'net';

export interface LifecycleEvent {
  time: number;
  source: LifecycleSource;
  category: LifecycleCategory;
  /** Stage label, e.g. "gate-mana:success" or "projectile-spawn". */
  label: string;
  /** Free-form payload detail string. */
  detail?: string;
  /** Original log status — only meaningful when source === 'fct'. */
  status?: 'success' | 'fail' | 'info';
}

export type CastStatus = 'in-flight' | 'completed' | 'rejected' | 'expired';

export interface Anomaly {
  type: 'windup-drift' | 'missing-projectile-impact' | 'orphan-event';
  message: string;
  severity: 'warn' | 'error';
}

export interface CastTimingBreakdown {
  inputToPredictionMs: number;
  predictionToServerConfirmMs: number;
  confirmToVfxMs: number | null;
  confirmToFctMs: number;
  totalMs: number;
}

export interface CastGroup {
  castId: string;        // synthetic — `${abilityId}:${startTime}:${sequence}`
  abilityId: string;
  startTime: number;
  endTime: number | null;
  status: CastStatus;
  rejection?: { reason: string; gateName: string; detail: string };
  events: LifecycleEvent[];
  anomalies: Anomaly[];
  timingBreakdown?: CastTimingBreakdown;
}

export interface UseCastLifecycleStreamOptions {
  /** Max number of groups retained (newest wins). Default 32. */
  maxGroups?: number;
  /** Inter-entry gap (ms) above which a new group starts for the same abilityId. Default 2000. */
  groupGapMs?: number;
  /** Tolerance (ms) for windup-drift anomaly detection. Default 100. */
  windupDriftToleranceMs?: number;
  /** Stop ingestion (paused state). Default false. */
  paused?: boolean;
}

export interface UseCastLifecycleStreamResult {
  groups: CastGroup[];
  paused: boolean;
  pause: () => void;
  resume: () => void;
  clear: () => void;
}

const DEFAULT_OPTS: Required<Omit<UseCastLifecycleStreamOptions, 'paused'>> = {
  maxGroups: 32,
  groupGapMs: 2000,
  windupDriftToleranceMs: 100,
};

const PROJECTILE_HIT_TIMEOUT_MS = 5000;

/**
 * React list keys must remain unique even when a multi-hit ability completes
 * several cast groups inside the same millisecond. Time alone is not a safe
 * discriminator: fan_of_knives routinely emits multiple events with one
 * DebugFCT timestamp.
 */
export function makeCastId(abilityId: string, startTime: number, sequence: number): string {
  return `${abilityId}:${startTime}:${sequence}`;
}

/** Server `debugCombat.reason` values that mean "the hit landed", not
 *  "the cast was refused". Distinct from rejection reasons so the
 *  lifecycle monitor doesn't mislabel successes as failures. */
const SERVER_SUCCESS_REASONS = new Set(['hit_success', 'kill']);

function getOneWayLatencyEstimate(): number {
  const diag = (typeof window !== 'undefined' ? (window as any).__HELIX_MP_DIAGNOSTICS__ : null) as
    | { clockSync?: { oneWayLatencyMs?: number } }
    | undefined;
  return diag?.clockSync?.oneWayLatencyMs ?? 0;
}

function computeCastTimingBreakdown(group: CastGroup): CastTimingBreakdown | undefined {
  if (group.events.length === 0) return undefined;
  const startTime = group.startTime;
  // Find key milestone events by label
  const executeEvent = group.events.find((e) => e.label === 'execute' || e.label.startsWith('projectile-spawn'));
  const serverConfirmEvent = group.events.find((e) => e.label.startsWith('server:hit_success') || e.label.startsWith('server:kill'));
  const firstVfxEvent = group.events.find((e) => e.label.startsWith('vfx:'));
  const fctEvent = group.events.find((e) => e.label.startsWith('damage-applied') || e.label.startsWith('projectile-hit'));

  if (!executeEvent) return undefined;

  // input→prediction: prefer event-based diff, but if both events
  // fired in the same frame (diff < 5ms) use PhaseTimingTracker's
  // latest phase1 for a more realistic input-latency number.
  let inputToPredictionMs = executeEvent.time - startTime;
  if (inputToPredictionMs < 5) {
    const pt = PhaseTimingTracker.latest();
    if (pt && typeof pt.phase1Ms === 'number' && pt.phase1Ms > 0) {
      inputToPredictionMs = pt.phase1Ms;
    }
  }

  // prediction→server-confirm: if we have the server confirm event,
  // use the event diff. For in-flight casts (no confirm yet), fall
  // back to the one-way latency estimate from ClockSync so the card
  // still shows a meaningful number.
  let predictionToServerConfirmMs = serverConfirmEvent
    ? serverConfirmEvent.time - executeEvent.time
    : getOneWayLatencyEstimate();

  const confirmToVfxMs = serverConfirmEvent && firstVfxEvent
    ? firstVfxEvent.time - serverConfirmEvent.time
    : null;
  const confirmToFctMs = serverConfirmEvent && fctEvent
    ? fctEvent.time - serverConfirmEvent.time
    : (fctEvent ? fctEvent.time - executeEvent.time : 0);
  const endTime = group.endTime ?? (fctEvent ? fctEvent.time : (serverConfirmEvent ? serverConfirmEvent.time : executeEvent.time));
  const totalMs = endTime - startTime;

  return {
    inputToPredictionMs: Math.max(0, inputToPredictionMs),
    predictionToServerConfirmMs: Math.max(0, predictionToServerConfirmMs),
    confirmToVfxMs: confirmToVfxMs !== null ? Math.max(0, confirmToVfxMs) : null,
    confirmToFctMs: Math.max(0, confirmToFctMs),
    totalMs: Math.max(0, totalMs),
  };
}

/**
 * Parse a DebugFCTEntry's text into structured cast info.
 * Format: `${abilityId}:${stage} — ${detail}` (` — ` separator optional).
 * Returns null if the text doesn't match the cast convention.
 *
 * Strict shape: ability id must be `[a-z][a-z0-9_]*` (TOML id
 * convention) and the stage character right after the colon must NOT
 * be a space — i.e. `fireball:gate-mana` matches but
 * `Impact: physical heavy` (the damage-feedback line from
 * DamagePresentationService) is rejected. Without this filter the
 * lifecycle monitor used to invent fake casts grouped under the
 * literal abilityId "Impact". The monitor's caller also filters by
 * `entry.channel === 'cast'` for a second line of defense.
 */
const ABILITY_ID_PATTERN = /^[a-z][a-z0-9_]*$/;

export function parseCastFCTEntry(entry: DebugFCTEntry): {
  abilityId: string;
  stage: string;
  detail?: string;
} | null {
  const sep = entry.text.indexOf(':');
  if (sep <= 0) return null;
  const abilityId = entry.text.slice(0, sep);
  if (!ABILITY_ID_PATTERN.test(abilityId)) return null;
  const rest = entry.text.slice(sep + 1);
  // Stage must not start with a space — distinguishes `fireball:gate`
  // from `Impact: physical heavy`. The caller also channel-filters,
  // but this is the structural check for cast text shape.
  if (rest.length === 0 || rest.startsWith(' ')) return null;
  const dashIdx = rest.indexOf(' — ');
  if (dashIdx === -1) {
    return { abilityId, stage: rest };
  }
  return {
    abilityId,
    stage: rest.slice(0, dashIdx),
    detail: rest.slice(dashIdx + 3),
  };
}

function categoryFromChannel(channel: DebugFCTEntry['channel']): LifecycleCategory {
  if (channel === 'vfx') return 'vfx';
  if (channel === 'combat') return 'combat';
  return 'cast';
}

export function useCastLifecycleStream(
  options: UseCastLifecycleStreamOptions = {},
): UseCastLifecycleStreamResult {
  const opts = { ...DEFAULT_OPTS, ...options };
  const [groups, setGroups] = useState<CastGroup[]>([]);
  const [paused, setPaused] = useState<boolean>(options.paused ?? false);
  // Mutable ref so the FCT/EventBus listener captures the latest groups
  // without re-subscribing each render.
  const groupsRef = useRef<CastGroup[]>([]);
  groupsRef.current = groups;
  const pausedRef = useRef(paused);
  pausedRef.current = paused;
  const nextGroupSequenceRef = useRef(0);

  const ingest = useCallback((event: LifecycleEvent, abilityId: string, rejection?: CastGroup['rejection']) => {
    if (pausedRef.current) return;
    const current = [...groupsRef.current];

    // Find an in-flight group for this ability whose newest event is
    // within the gap window — extend it. Otherwise start a new group.
    let groupIdx = -1;
    for (let i = current.length - 1; i >= 0; i--) {
      const g = current[i];
      if (g.abilityId !== abilityId) continue;
      if (g.status !== 'in-flight') continue;
      const lastTime = g.events.length > 0 ? g.events[g.events.length - 1].time : g.startTime;
      if (event.time - lastTime <= opts.groupGapMs) {
        groupIdx = i;
        break;
      }
    }

    if (groupIdx === -1) {
      // New group.
      const group: CastGroup = {
        castId: makeCastId(abilityId, event.time, nextGroupSequenceRef.current++),
        abilityId,
        startTime: event.time,
        endTime: null,
        status: 'in-flight',
        events: [event],
        anomalies: [],
      };
      if (rejection) {
        group.status = 'rejected';
        group.rejection = rejection;
        group.endTime = event.time;
      }
      current.push(group);
    } else {
      const group = { ...current[groupIdx] };
      group.events = [...group.events, event];
      // Status transitions
      if (rejection) {
        group.status = 'rejected';
        group.rejection = rejection;
        group.endTime = event.time;
      } else if (event.label.startsWith('damage-applied') || event.label.startsWith('projectile-hit')) {
        group.status = 'completed';
        group.endTime = event.time;
        group.timingBreakdown = computeCastTimingBreakdown(group);
        if (group.timingBreakdown) {
          const meta = abilityRegistry.getMetadata(group.abilityId);
          CastLagStore.push({
            abilityId: group.abilityId,
            abilityName: meta?.name || group.abilityId,
            breakdown: group.timingBreakdown,
            renderedAt: Date.now(),
            x: 0,
            y: 0,
          });
          // Also expose the latest abilityId on window so LatencyOverlay
          // can correlate spikes with casts.
          if (typeof window !== 'undefined') {
            (window as any).__HELIX_LAST_CAST_ABILITY_ID = group.abilityId;
          }
        }
      }
      // Anomaly detection — windup drift.
      if (event.label === 'projectile-spawn' || event.label.startsWith('projectile-spawn')) {
        const meta = abilityRegistry.getMetadata(abilityId);
        const expected = meta?.castTime ?? 0;
        if (expected > 0) {
          const gateMana = group.events.find((e) => e.label === 'gate-mana:success');
          const reference = gateMana ?? group.events[0];
          const observed = event.time - reference.time;
          if (Math.abs(observed - expected) > opts.windupDriftToleranceMs) {
            group.anomalies = [
              ...group.anomalies,
              {
                type: 'windup-drift',
                message: `windUpMs expected ${expected}ms, observed ${observed}ms`,
                severity: 'warn',
              },
            ];
          }
        }
      }
      current[groupIdx] = group;
    }

    // Trim ring buffer.
    while (current.length > opts.maxGroups) current.shift();

    groupsRef.current = current;
    setGroups(current);
  }, [opts.groupGapMs, opts.maxGroups, opts.windupDriftToleranceMs]);

  // ── DebugFCT subscription ─────────────────────────────────────
  useEffect(() => {
    const unsubscribe = DebugFCT.subscribe((entry: DebugFCTEntry) => {
      // Channel guard: only the 'cast' channel carries the cast
      // pipeline narrative. The 'combat' channel emits damage-feedback
      // text like "Impact: physical heavy" which would otherwise
      // create fake "Impact" cast groups (2026-04-30 fix).
      if (entry.channel !== 'cast') return;
      const parsed = parseCastFCTEntry(entry);
      if (!parsed) return;

      const isRejection = entry.level === 'fail' && parsed.stage.startsWith('gate-');
      const event: LifecycleEvent = {
        time: entry.time,
        source: 'fct',
        category: isRejection ? 'rejection' : categoryFromChannel(entry.channel),
        label: `${parsed.stage}${entry.level === 'success' ? ':success' : entry.level === 'fail' ? ':fail' : ''}`,
        detail: parsed.detail,
        status: entry.level,
      };

      const rejection = isRejection
        ? {
            reason: parsed.stage.replace(/^gate-/, ''),
            gateName: parsed.stage,
            detail: parsed.detail ?? '',
          }
        : undefined;

      ingest(event, parsed.abilityId, rejection);
    });
    return () => unsubscribe();
  }, [ingest]);

  // ── VFX spawn subscription ────────────────────────────────────
  // Cast pipelines that emit multiple visuals in sequence (rogue
  // disappear → smoke → flash → backstab; mage fireball → trail →
  // impact burst) had no representation in the lifecycle panel —
  // DJ couldn't tell what VFX actually played vs what was supposed to.
  // Subscribe to `vfx:spawn` (the canonical "play an effect" channel)
  // and time-correlate to the most recent in-flight cast group: if a
  // VFX fires within `groupGapMs` of the cast's last event, attribute
  // it to that cast. Cross-cast bleed is theoretically possible
  // (back-to-back spells) but at the scale of one local caster's
  // sequential abilities, the timing window is tight enough.
  //
  // Also catches `vfx:status_effect` (apply/remove of poison cloud,
  // burn aura, etc.) so DOT-applying abilities show their persistent
  // visuals in the timeline.
  useEffect(() => {
    const bus = EventBus.getInstance();
    const attribute = (effectId: string, kind: 'spawn' | 'persistent', extra?: string) => {
      const now = Date.now();
      const current = groupsRef.current;
      // Find the newest in-flight group whose last event is within the
      // gap window. Walk from end so most-recent cast wins.
      for (let i = current.length - 1; i >= 0; i--) {
        const g = current[i];
        if (g.status !== 'in-flight') continue;
        const lastTime = g.events.length > 0 ? g.events[g.events.length - 1].time : g.startTime;
        if (now - lastTime > opts.groupGapMs) continue;
        // Attribute this VFX to that group via ingest.
        const event: LifecycleEvent = {
          time: now,
          source: 'event-bus',
          category: 'vfx',
          label: kind === 'spawn' ? `vfx:${effectId}` : `vfx:${effectId}:${extra ?? 'persistent'}`,
          detail: extra && kind === 'spawn' ? extra : undefined,
        };
        ingest(event, g.abilityId);
        return;
      }
      // No in-flight cast within the window → drop. Untracked VFX
      // (zone particles, ambient effects) shouldn't pollute the
      // ability lifecycle view.
    };

    const offSpawn = bus.on('vfx:spawn', (data: any) => {
      const effectId = String(data?.effectId ?? '?');
      const targetId = data?.targetId ?? data?.casterId;
      attribute(effectId, 'spawn', targetId ? `→${targetId}` : undefined);
    });
    const offStatus = bus.on('vfx:status_effect', (data: any) => {
      const effectId = String(data?.effectId ?? '?');
      const action = data?.active ? 'on' : 'off';
      attribute(effectId, 'persistent', action);
    });
    return () => { offSpawn(); offStatus(); };
  }, [ingest, opts.groupGapMs]);

  // ── Server-side debugCombat subscription ──────────────────────
  // CombatHandler emits debugCombat for BOTH success ('hit_success',
  // 'kill') AND rejections ('out_of_range', 'pvp_disabled_on_map',
  // etc.). Map the success reasons to a 'completed' lifecycle event
  // and the rest to rejections — otherwise every successful hit gets
  // logged as "Rejected — server: hit_success", which is wrong + noisy.
  useEffect(() => {
    const bus = EventBus.getInstance();
    const off = bus.on('net:message:debugCombat', (msg: any) => {
      const reason = msg?.reason ?? 'unknown';
      const abilityId = msg?.abilityId ?? msg?.ability_id ?? 'unknown';
      const isSuccess = SERVER_SUCCESS_REASONS.has(reason);

      // Detail formatting:
      //   range failures   → "350px / 450px range" (clear distance vs gate)
      //   los_blocked      → "blocked by <type> @(x,y)→(x,y)"
      //   hit success      → "<dmg> Physical → <npcName>" (compact summary)
      //   else             → JSON dump (preserve all fields for triage)
      const range = msg?.range ?? msg?.maxRange;
      let detail: string;
      if (reason === 'los_blocked') {
        const blocker = msg?.blockerType ?? 'platform';
        const fromPart = msg?.from ? `(${msg.from.x},${msg.from.y})` : '?';
        const toPart = msg?.to ? `(${msg.to.x},${msg.to.y})` : '?';
        detail = `blocked by ${blocker} ${fromPart}→${toPart}`;
      } else if (msg?.distance != null && range != null) {
        const cmp = isSuccess ? '/' : '>';
        detail = `${msg.distance}px ${cmp} ${range}px range`;
      } else if (isSuccess && msg?.damage != null) {
        const tgt = msg?.npcName ?? msg?.targetName ?? msg?.npcId ?? '?';
        detail = `${msg.damage}${msg.critical ? ' CRIT' : ''} → ${tgt}`;
      } else {
        detail = JSON.stringify(msg);
      }

      const event: LifecycleEvent = {
        time: Date.now(),
        source: 'net',
        category: isSuccess ? 'combat' : 'rejection',
        label: isSuccess ? `server:${reason}` : `server-reject:${reason}`,
        detail,
        status: isSuccess ? 'success' : 'fail',
      };
      ingest(event, abilityId, isSuccess ? undefined : {
        reason,
        gateName: 'server',
        detail: event.detail ?? '',
      });
    });
    return () => off();
  }, [ingest]);

  // ── Expire stale in-flight groups (no events for too long) ────
  useEffect(() => {
    const id = setInterval(() => {
      if (pausedRef.current) return;
      const now = Date.now();
      let mutated = false;
      const next = groupsRef.current.map((g) => {
        if (g.status !== 'in-flight') return g;
        const lastTime = g.events.length > 0 ? g.events[g.events.length - 1].time : g.startTime;
        if (now - lastTime > PROJECTILE_HIT_TIMEOUT_MS) {
          mutated = true;
          // If we saw projectile-spawn but never hit, flag anomaly.
          const sawSpawn = g.events.some((e) => e.label.startsWith('projectile-spawn'));
          const sawHit = g.events.some(
            (e) => e.label.startsWith('damage-applied') || e.label.startsWith('projectile-hit'),
          );
          const anomalies = [...g.anomalies];
          if (sawSpawn && !sawHit) {
            anomalies.push({
              type: 'missing-projectile-impact',
              message: 'projectile-spawn fired but no damage-applied / projectile-hit within 5s',
              severity: 'warn',
            });
          }
          return { ...g, status: 'expired' as CastStatus, endTime: lastTime, anomalies };
        }
        return g;
      });
      if (mutated) {
        groupsRef.current = next;
        setGroups(next);
      }
    }, 1000);
    return () => clearInterval(id);
  }, []);

  const pause = useCallback(() => setPaused(true), []);
  const resume = useCallback(() => setPaused(false), []);
  const clear = useCallback(() => {
    groupsRef.current = [];
    nextGroupSequenceRef.current = 0;
    setGroups([]);
  }, []);

  return { groups, paused, pause, resume, clear };
}
